/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/naming-convention */
import axios from 'axios';
import { setToken } from '../redux/parentDataSlice';
import store from '../redux/store';

axios.defaults.baseURL = process.env.REACT_APP_API_URL;

// attach token automatically
axios.interceptors.request.use((config) => {
  const token = store.getState().parentData.token;
  if (token && config.headers) {
    config.headers['Authorization'] = 'Bearer ' + token.accessToken;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 403 && !originalRequest._retry) {
      originalRequest._retry = true;
      const storedTokens = store.getState().parentData.token;
      if (storedTokens?.refreshToken) {
        try {
          const res = await axios.post('/auth/refresh', {
            refreshToken: storedTokens?.refreshToken,
          });

          const tokens = {
            accessToken: res.data.accessToken,
            refreshToken: res.data.refreshToken,
          };

          store.dispatch(setToken(tokens));
          originalRequest.headers['Authorization'] = 'Bearer ' + tokens.accessToken;

          return axios(originalRequest);
        } catch (refreshError) {
          console.error('Refresh failed:', refreshError);
          store.dispatch(setToken({ accessToken: null, refreshToken: null }));
        }
      }
    }
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  },
);

export const handleCanvasLinksAPI = async (
  optionValue: string,
  env: string,
  courseId: string,
  query_params: string,
  signal?: AbortSignal,
) => {
  const data = {
    method: 'POST',
    endPoint: '/canvas/api/graphql-proxy',
    body: {
      details: {
        fieldName: 'sortingCourseLinks',
        envName: env,
        arguments: {
          optionValue: optionValue.toLowerCase(),
          courseId: courseId,
          query_params: query_params,
        },
      },
    },
    signal,
  };
  return await callApi(data);
};
export const getCourseNavigationLinks = async (
  courseId: string,
  pageIndex: number,
  env: string,
  signal?: AbortSignal,
) => {
  const data = {
    method: 'POST',
    endPoint: '/canvas/api/graphql-proxy',
    body: {
      details: {
        fieldName: 'canvasCourseNavigation',
        envName: env,
        arguments: {
          courseId: courseId,
          pageIndex: pageIndex,
          pageItems: 10,
        },
      },
    },
    signal,
  };
  return await callApi(data);
};

export const getCanvasCourseAudio = async (courseId: string, env: string, signal?: AbortSignal) => {
  const data = {
    method: 'POST',
    endPoint: '/canvas/api/graphql-proxy',
    body: {
      details: {
        fieldName: 'canvasCourseFiles',
        envName: env,
        arguments: {
          courseId: courseId,
          fileType: 'audio',
        },
      },
    },
    signal,
  };
  return await callApi(data);
};

export const getCanvasUserAudio = async (env: string, signal?: AbortSignal) => {
  const data = {
    method: 'POST',
    endPoint: '/canvas/api/graphql-proxy',
    body: {
      details: {
        fieldName: 'canvasUserFiles',
        envName: env,
        arguments: {
          fileType: 'audio',
        },
      },
    },
    signal,
  };
  return await callApi(data);
};

export const getAuthenticationToken = async (
  loreeUserEmail: string,
  userEmail: string,
  orgId: string,
) => {
  const dataToSend = {
    email: loreeUserEmail,
    lintUser: userEmail,
    orgId: orgId,
  };

  const apiDetails = {
    method: 'POST',
    endPoint: '/auth/get-token',
    body: dataToSend,
  };

  return await callApi(apiDetails);
};

// Org and User auth check
// Request generator for create content
export const userVerification = async (user: string, org_id: string) => {
  const userVerify = {
    method: 'POST',
    endPoint: '/user/verify',
    body: { user, org_id },
  };
  const verifiedUser = (await callApi(userVerify)) as any;
  return verifiedUser;
};

// To fetch the trackingId of each organization
export const fetchGaID = async (orgId: string | null) => {
  const data = {
    method: 'GET',
    endPoint: '/gaId',
    body: orgId,
  };
  const trackingId = await callApi(data);
  return trackingId;
};
// Fetching the library list
export const getLibrary = async () => {
  const data = {
    method: 'GET',
    endPoint: '/library',
  };
  const libraryResult = await callApi(data);
  return libraryResult;
};

// Request generator to fetch content
export const fetchContent = async (id: string | null, user: string | null) => {
  const apiData = {
    method: 'GET',
    endPoint: `/content/loree-view/${id}`,
    headers: { user_id: user },
  };
  return await callApi(apiData);
};

//view exact content
export const viewContent = async (id: string, type: string) => {
  const apiData = {
    method: 'GET',
    endPoint: `/content/view/${id}`,
    headers: { type },
  };
  return await callApi(apiData);
};

export const fetchCollabView = async (id: string) => {
  const apiData = {
    method: 'GET',
    endPoint: `/paginated-lint/api/${id}`,
  };
  return await callApi(apiData);
};

export const fetchTemplateView = async (id: string) => {
  const apiData = {
    method: 'GET',
    endPoint: `/templated-lint/api/${id}`,
  };
  return await callApi(apiData);
};

export const fetchCollabViewWithConfig = async (id: string, user: string) => {
  const apiData = {
    method: 'GET',
    endPoint: `/paginated-lint/api/component/${id}`,
    headers: { user_id: user },
  };
  return await callApi(apiData);
};

export const fetchTemplateViewWithConfig = async (id: string, user: string) => {
  const apiData = {
    method: 'GET',
    endPoint: `/templated-lint/api/component/${id}`,
    headers: { user_id: user },
  };
  return await callApi(apiData);
};

// Get Individual content
export const getContent = async (id: string, user: string) => {
  const data = {
    method: 'GET',
    endPoint: `/content/loree-view/${id}`,
    headers: { user_id: user },
  };
  return await callApi(data);
};

// Request generator for create content
export const createContent = async (data: {}) => {
  const apiData = {
    method: 'POST',
    endPoint: '/content',
    body: data,
  };
  return await callApi(apiData);
};

export const createOpenInteractive = async (data: {}, type: string) => {
  const apiData = {
    method: 'POST',
    endPoint: `/${type}/api/create`,
    body: data,
  };
  return await callApi(apiData);
};

export const updateOpenInteractive = async (data: { id: string; body: {} }, type: string) => {
  const apiData = {
    method: 'PATCH',
    endPoint: `/${type}/api/${data.id}`,
    body: data.body,
  };
  return await callApi(apiData);
};

export const titleCheck = async (data: {}) => {
  const apiData = {
    method: 'POST',
    endPoint: '/content/titlecheck',
    body: data,
  };
  return await callApi(apiData);
};

export const createGlobalContent = async (data: {}) => {
  const apiData = {
    method: 'POST',
    endPoint: '/api/loree/shareInteractiveContent',
    body: data,
  };
  return await callApi(apiData);
};

// Delete individual content
export const deleteContent = async (user_id: string, id?: string) => {
  const apiData = {
    method: 'DELETE',
    endPoint: `/content/loree-view/${id}`,
    headers: { user_id },
  };
  return await callApi(apiData);
};

// Edit individual content
export const editContent = (data: { id: string; body: {} }) => {
  const apiData = {
    method: 'PUT',
    endPoint: `/content/loree-view/${data.id}`,
    body: data.body,
  };
  return callApi(apiData);
};

//To Edit individual status of the created component
export const toggleActive = (user_id: string, status: boolean, id?: string, lib_id?: number) => {
  const apiData = {
    method: 'PUT',
    endPoint: `/content/update/${id}`,
    body: { id, user_id, lib_id, active: status },
  };
  return callApi(apiData);
};

// To upload the images form editor to s3
export const uploadToS3 = (file: {}, userDetails: { userId: string; orgId: string }) => {
  const apiData = {
    method: 'POST',
    endPoint: '/s3',
    headers: userDetails,
    body: file,
  };
  return callApi(apiData);
};

export const getUserInteractives = async (user_id: string, orgId: string) => {
  const data = {
    method: 'GET',
    endPoint: '/api/user/interactives',
    headers: { userid: user_id, orgid: orgId },
  };
  const contentResult = await callApi(data);
  return contentResult;
};

export const userVerify = async (user_id: string) => {
  const data = {
    method: 'GET',
    endPoint: '/api/user/verify',
    headers: { userid: user_id },
  };
  const contentResult = await callApi(data);
  return contentResult;
};

export const verifyOrg = async (org_id: string) => {
  const data = {
    method: 'GET',
    endPoint: '/api/org/verify',
    headers: { orgid: org_id },
  };
  const contentResult = await callApi(data);
  return contentResult;
};

export const getGlobalInteractives = async (userId: string, orgId: string) => {
  const data = {
    method: 'GET',
    endPoint: '/api/global/interactives',
    headers: { orgid: orgId, userid: userId },
  };
  const contentResult = await callApi(data);
  return contentResult;
};

export const getSharedInteractives = async (inputData: {
  orgid: string;
  accountid: string;
  userid: string;
}) => {
  const data = {
    method: 'GET',
    endPoint: '/api/shared/interactives',
    headers: inputData,
  };
  const contentResult = await callApi(data);
  return contentResult;
};

/* Will be deleted once all the images of the content are migrated to s3 */
export const getComponentById = async (id: string) => {
  const apiData = {
    method: 'GET',
    endPoint: `/content/component/${id}`,
  };

  return await callApi(apiData);
};

/* Will be deleted once all the images of the content are migrated to s3 */
export const migrateImagesTos3 = async (inputDetails: {}) => {
  const apiData = {
    method: 'POST',
    endPoint: '/s3/migrate',
    body: inputDetails,
  };

  return await callApi(apiData);
};

/* Will be deleted once all the images of the content are migrated to s3 */
export const saveUpdatedContent = (data: { id: string; parameters: {} }) => {
  const apiData = {
    method: 'PUT',
    endPoint: `/content/migrate/${data.id}`,
    body: data.parameters,
  };
  return callApi(apiData);
};

/* will be deleted when the content is migrated from v1 to v2 */

export const fetchDuplicatedUsers = (orgId: string) => {
  return callApi({
    method: 'GET',
    endPoint: `/migration/duplicateUser`,
    headers: { org_id: orgId },
  });
};

export const updateUserId = (inputData: {}) => {
  return callApi({
    method: 'PUT',
    endPoint: `/migration/updateContent`,
    body: inputData,
  });
};

export const deleteDuplicatedUsers = (inputData: {}) => {
  return callApi({
    method: 'DELETE',
    endPoint: `/migration/deleteUser`,
    body: inputData,
  });
};

export const fetchImages = async (org_id: string | null) => {
  const data = {
    method: 'GET',
    endPoint: `/images/${org_id}`,
  };
  const contentResult = await callApi(data);
  return contentResult;
};
// Send request to API
const callApi = async (data: any) => {
  const response = await axios({
    method: data.method,
    url: data.endPoint,
    headers: data.headers,
    data: data.body,
    signal: data.signal,
  });
  return response.data;
};
